import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { grey } from '@mui/material/colors';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { FormattedMessage } from 'react-intl';
import moment from 'moment';
import { parseWeekday, recurrenceTypes } from '~/components/RecurrenceRules';
import ErrorPage from '~/components/ErrorPage';
import AvailabilityDelete from '~/components/AvailabilityDelete';
import Inset from '~/modules/ui-kit/spacings/inset/inset';
import Stack from '~/modules/ui-kit/spacings/stack/stack';
import messages from './messages';
import { useCutoffDates } from '~/hooks/api/useCutoffDates';
import { useRecurrentApplications } from '~/hooks/api/useRecurrentApplications';
import { useLocation, Link } from '@tanstack/react-router';

type WeeklyRecurrentApplicationRule = {
  interval: number;
  type: 1;
};

type MonthlyRecurrentApplicationRule = {
  type: 2;
  nthWeekdayOfMonth: number;
  weekday: 'SU' | 'MO' | 'TU' | 'WE' | 'TH' | 'FR' | 'SA';
};

export type RecurrentApplicationRule =
  | WeeklyRecurrentApplicationRule
  | MonthlyRecurrentApplicationRule;

const WeeklyRecurrenceText = (props: {
  rule: WeeklyRecurrentApplicationRule;
}) => (
  <FormattedMessage
    {...messages.regularApplicationWeekly}
    values={{
      nthWeek: props.rule.interval,
    }}
  />
);

const MonthlyRecurrenceText = (props: {
  rule: MonthlyRecurrentApplicationRule;
}) => (
  <FormattedMessage
    {...messages.regularApplicationMonthly}
    values={{
      nth: props.rule.nthWeekdayOfMonth,
      weekday: moment()
        .isoWeekday(parseWeekday(props.rule.weekday))
        .format('dddd'),
    }}
  />
);

const getText = (recurrence: RecurrentApplicationRule) => {
  switch (recurrence.type) {
    case recurrenceTypes.weekly:
      return <WeeklyRecurrenceText rule={recurrence} />;
    case recurrenceTypes.nthWeekdayOfMonth:
      return <MonthlyRecurrenceText rule={recurrence} />;
    default:
      return null;
  }
};

export type AvailabilityListProps = {
  publisherId: number;
};

const AvailabilityList = (props: AvailabilityListProps) => {
  const location = useLocation();
  const recurrentApplicationsQuery = useRecurrentApplications(
    props.publisherId
  );
  const cutOffDatesQuery = useCutoffDates();
  return (
    <>
      {recurrentApplicationsQuery.error ? (
        <ErrorPage
          onRetry={recurrentApplicationsQuery.refetch}
          isLoading={recurrentApplicationsQuery.isLoading}
        />
      ) : null}
      {cutOffDatesQuery.error ? (
        <ErrorPage
          onRetry={cutOffDatesQuery.refetch}
          isLoading={cutOffDatesQuery.isLoading}
        />
      ) : null}
      {recurrentApplicationsQuery.data?.length === 0 ? (
        <Inset scale="l">
          <Stack scale="l" alignItems="center">
            <Box
              sx={{ borderRadius: '99999999px', backgroundColor: grey[100] }}
            >
              <Inset scale="xl">
                <Typography component="div" color="textSecondary">
                  <EventAvailableIcon fontSize="large" />
                </Typography>
              </Inset>
            </Box>
            <div>
              <Typography component="p" color="textSecondary" align="center">
                <FormattedMessage {...messages.emptyStateText} />
              </Typography>
            </div>
            <div>
              <Button
                variant="contained"
                color="primary"
                component={Link}
                to={`/availability/create?publisherId=${
                  props.publisherId
                }&from=${encodeURIComponent(location.href)}`}
              >
                <FormattedMessage {...messages.addAvailability} />
              </Button>
            </div>
          </Stack>
        </Inset>
      ) : null}
      {recurrentApplicationsQuery.data &&
      recurrentApplicationsQuery.data.length > 0 &&
      cutOffDatesQuery.data ? (
        <List>
          {recurrentApplicationsQuery.data.map((recurrentApplication) => (
            <ListItem
              key={recurrentApplication.id}
              secondaryAction={
                <AvailabilityDelete
                  recurrentApplicationId={recurrentApplication.id}
                  publisherId={props.publisherId}
                  shiftsReleasedUntil={
                    cutOffDatesQuery.data.shiftsReleasedUntil
                      ? new Date(cutOffDatesQuery.data.shiftsReleasedUntil)
                      : new Date()
                  }
                />
              }
            >
              <ListItemButton
                component={Link}
                to={`/availability/${recurrentApplication.id}?publisherId=${
                  props.publisherId
                }&from=${encodeURIComponent(location.href)}`}
              >
                <ListItemText
                  secondary={getText(
                    recurrentApplication.rule as RecurrentApplicationRule
                  )}
                >
                  {moment(recurrentApplication.start).format('dddd LT')} -{' '}
                  {moment(recurrentApplication.end).format('LT')}
                </ListItemText>
              </ListItemButton>
            </ListItem>
          ))}
          <Box
            sx={{
              padding: 2,
            }}
          >
            <Button
              variant="contained"
              color="primary"
              component={Link}
              to={`/availability/create?publisherId=${
                props.publisherId
              }&from=${encodeURIComponent(location.href)}`}
            >
              <FormattedMessage {...messages.addAvailability} />
            </Button>
          </Box>
        </List>
      ) : null}
    </>
  );
};
export default AvailabilityList;
